import { GraduationCap, ChevronRight, ExternalLink } from "lucide-react";
import { toast } from "../components/ui/sonner";

export const LearningCard = ({ pathways, aiLevel, onItemClick, onBrowseAll }) => {
  if (!pathways || pathways.length === 0) {
    return (
      <section className="card-surface p-5" data-testid="learning-card-empty">
        <h3 className="text-title-lg font-headline text-[#F8FAFC] mb-4">Learning Pathways</h3>
        <p className="text-[#94A3B8] text-sm">No learning pathways available for your level.</p>
      </section>
    );
  }

  const getLevelColor = (level) => {
    switch (level) {
      case "beginner": return "bg-[#14B8A6]/10 text-[#14B8A6]";
      case "intermediate": return "bg-[#6D28D9]/10 text-[#A78BFA]";
      case "advanced": return "bg-amber-500/10 text-amber-400";
      default: return "bg-white/5 text-[#94A3B8]";
    }
  };

  return (
    <section className="card-surface p-5" data-testid="learning-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-[#14B8A6]" />
          <h3 className="text-title-lg font-headline text-[#F8FAFC]">Learning Pathways</h3>
        </div>
        <button
          onClick={onBrowseAll}
          className="text-sm text-[#14B8A6] font-medium hover:underline flex items-center gap-1"
          data-testid="learning-browse-all"
        >
          Browse All <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className={`grid gap-4 ${
        pathways.length === 1 ? "grid-cols-1" 
        : pathways.length === 2 ? "grid-cols-1 sm:grid-cols-2" 
        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      }`}>
        {pathways.slice(0, 3).map((pathway, idx) => (
          <div
            key={pathway.id || idx}
            onClick={() => onItemClick && onItemClick(pathway)}
            className="group p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] hover:border-[#14B8A6]/20 transition-all duration-300 cursor-pointer flex flex-col"
            data-testid={`learning-pathway-${idx}`}
          >
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getLevelColor(pathway.level)}`}>
                  {pathway.level}
                </span>
                {pathway.platform && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] text-[#94A3B8] flex items-center gap-1">
                    <ExternalLink className="w-2.5 h-2.5" /> {pathway.platform}
                  </span>
                )}
              </div>
              <span className="text-xs text-[#94A3B8]">{pathway.estimated_duration}</span>
            </div>

            <h4 className="text-sm font-semibold text-[#F8FAFC] mb-1.5 group-hover:text-[#14B8A6] transition-colors line-clamp-2">
              {pathway.title}
            </h4>

            {pathway.instructor && (
              <p className="text-xs text-[#94A3B8] mb-2">{pathway.instructor}</p>
            )}

            <p className="text-xs text-[#94A3B8] mb-4 line-clamp-2 flex-1">
              {pathway.summary}
            </p>

            {pathway.progress_percent > 0 && (
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-[#94A3B8]">Progress</span>
                  <span className="text-[#F8FAFC] font-medium">{pathway.progress_percent}%</span>
                </div>
                <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div className="h-full progress-gradient rounded-full transition-all duration-500" style={{ width: `${pathway.progress_percent}%` }} />
                </div>
              </div>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onItemClick && onItemClick(pathway);
              }}
              className="w-full py-2 px-4 rounded-md bg-[#14B8A6]/10 text-[#14B8A6] text-sm font-medium hover:bg-[#14B8A6]/20 transition-colors flex items-center justify-center gap-2 mt-auto"
              data-testid={`learning-cta-${idx}`}
            >
              {pathway.progress_percent > 0 ? 'Continue' : 'Start Course'}
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};
